import { MonitorSeriesNames } from '../../../location-card.constants';
import { alarmToggleActions, disableField, enableField } from '../../actions';
import { Section, SelectorIdentifier } from '../../models';
import { TritonSectionIds } from './base-config-models';
import { DEFAULT_FLOW_LOSS_PERCENTAGE } from 'app/shared/constant';
import { ConfigureAlarmsFieldId } from '../common.models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { field } from '../../core/field-schema';
import { required, maxLength, pattern, min, max } from '../../core/validator-builders';
import { SamplerTypeOption } from '../../../models/location-card.model';
import { AlarmDevice, FlowLossAlarmDevice, TritonLocationCardState } from './models';
import { NonSensorDeviceNames } from 'app/shared/enums/device-type-code';
import { roundToDecimal } from '../../models/utils';

export const getValidators = function (isMetric: boolean) {
    const validators = [];

    if (isMetric) {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
            }),
        ];
    } else {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR',
            }),
        ];
    }
};

/**
 * Triton Alarms Configuration Section
 */
export const configureAlarms = (state: TritonLocationCardState): Section => {
    const settings = state.settings;
    const config = state.monitorConfiguration;

    const alarmData: AlarmDevice = config?.devices.find((v) => v.name === NonSensorDeviceNames.Alarm1) as AlarmDevice;
    const flowLossData = config?.devices ? (config.devices.find((v) => v.name === NonSensorDeviceNames.FlowLoss1) as FlowLossAlarmDevice) : null;

    const tritonSeries = state.allMonitorSeries.find(
        (v) => v.value.toLowerCase() === MonitorSeriesNames.triton.toLowerCase(),
    );

    const depthAlarmValidators = [required(), ...getValidators(settings.isMetric)];
    const highFlowValidators = [
        required(),
        min({ value: 0 }),
        max({ value: 100000 }),
        pattern(
            settings.isMetric
                ? {
                      value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                      message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
                  }
                : {
                      value: REGEX_CONFIG.numeric0_100000With3DecimalPlacesPattern,
                      message: 'COMMON.NUMERIC_PRECISION_ERROR_3_DECIMALS',
                  },
        ),
    ];

    return {
        info: {
            id: TritonSectionIds.ConfigureAlarms,
            title: 'ADD_EDIT.ADD_EDIT_CONFIGURE',
            displayTitle: true,
            expandable: true,
            expanded: false,
            preserveOldValues: false,
            panelClass: !state.permissions.isMonitorEditor ? 'disable-field' : undefined,
        },
        rows: [
            // Low Level Alarm Row
            {
                info: {
                    id: 'low-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.LowLevelEnable, 'lowLevelEnable', {
                        placeholder: 'COMMON.LOW_LEVEL_ALARM',
                        value: alarmData?.lowLevelEnable,
                        panelClass: 'alarm-slide-toggle',
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.LowLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.LowLevelThreshold, 'lowLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_LOW_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(alarmData?.lowLevelThreshold, 1),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData && alarmData.lowLevelEnable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // Full Pipe Alarm Row
            {
                info: {
                    id: 'full-pipe-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.FullPipeEnable, 'fullPipeEnable', {
                        placeholder: 'COMMON.FULL_PIPE_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.fullPipeEnable ?? null,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold }),
                            ],
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.FullPipeThreshold, 'fullPipethreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_FULL_PIPE',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        readonly: true,
                        value: roundToDecimal(alarmData?.fullPipeThreshold, settings.isMetric ? 1 : 2),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData && alarmData.fullPipeEnable ? false : true,
                        validators: getValidators(settings.isMetric),
                    }),
                ],
            },

            // High Level Alarm Row
            {
                info: {
                    id: 'high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighLevelEnable, 'highLevelEnable', {
                        placeholder: 'COMMON.HIGH_LEVEL_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.highLevelEnable ?? null,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighLevelThreshold, 'highLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(alarmData?.highLevelThreshold, 1),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData && alarmData.highLevelEnable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High-High Level Alarm Row
            {
                info: {
                    id: 'high-high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighHighEnable, 'highHighEnable', {
                        placeholder: 'COMMON.HIGH_HIGH_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.highHighEnable ?? null,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighHighThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighHighThreshold, 'highHighThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_HIGH',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(alarmData?.highHighThreshold, settings.isMetric ? 1 : 2),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: alarmData && alarmData.highHighEnable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High Flow Alarm Row
            {
                info: {
                    id: 'high-flow-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighFlowEnable, 'highFlowEnable', {
                        placeholder: 'COMMON.HIGH_FLOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.highFlowEnable ?? null,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighFlowThreshold, highFlowValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighFlowThreshold, 'highFlowThreshold', {
                        placeholder: 'COMMON.QUANTITY_THRESHOLD_OF',
                        placeholderSuffix: settings.flowUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.highFlowThreshold ?? null,
                        disabled: alarmData && alarmData.highFlowEnable ? false : true,
                        validators: highFlowValidators,
                    }),
                ],
            },

            // Overflow Alarm Row - no inputs to enable/disable
            {
                info: {
                    id: 'overflow-alarm-row',
                    panelClass: 'each-configure-alarm margin-t-b-15',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.OverflowEnable, 'overflowEnable', {
                        placeholder: 'COMMON.OVERFLOW_ALARMS',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.overflowEnable,
                    }),
                ],
            },

            // Battery Low Alarm Row
            {
                info: {
                    id: 'battery-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.BatteryLowEnable, 'batteryLowEnable', {
                        placeholder: 'COMMON.BATTERY_LOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData?.batteryLowEnable ?? null,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.BatteryLowThreshold })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.BatteryLowThreshold }),
                            ],
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.BatteryLowThreshold, 'batteryLowThreshold', {
                        placeholder: 'COMMON.BATTERY_THRESHOLD_INPUT_PLACEHOLDER',
                        placeholderSuffix: settings.voltUnit,
                        panelClass: 'alarm-input',
                        value: alarmData?.batteryLowThreshold ?? tritonSeries.batteryLowInt,
                        disabled: alarmData && alarmData.batteryLowEnable ? false : true,
                        validators: [
                            required(),
                            maxLength({ value: 100 }),
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.installationMetricValuePattern
                                    : REGEX_CONFIG.installationValuePattern,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                        ],
                    }),
                ],
            },

            // Flow Loss Alarm Row
            {
                info: {
                    id: 'flow-loss-alarm-row',
                    panelClass: 'each-configure-alarm flow-loss-alaram',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.FlowLossAlarm, 'flowLossAlarm', {
                        placeholder: 'COMMON.FLOW_LOSS_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: flowLossData ? true : false,
                        events: {
                            onChange: (value) => [
                                // Enable/disable all fields in this row
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.FlowLossType })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.FlowLossType }),
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.Percent })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.Percent }),
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.Sensitivity })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.Sensitivity }),
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.ClearPatternHistory })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.ClearPatternHistory }),
                            ],
                        },
                    }),
                    field.select<SamplerTypeOption, number>(ConfigureAlarmsFieldId.FlowLossType, 'flowLossType', {
                        label: 'COMMON.TYPE',
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.name,
                        panelClass: 'alarm-input-70',
                        value: flowLossData ? flowLossData.flowLossType : 0,
                        disabled: flowLossData ? false : true,
                        optionsSelector: SelectorIdentifier.FlowLossAlarmTypes,
                    }),
                    field.select<SamplerTypeOption, number>(ConfigureAlarmsFieldId.Percent, 'percent', {
                        panelClass: 'alarm-input-70',
                        label: 'COMMON.PERCENTAGE',
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.name,
                        optionsSelector: SelectorIdentifier.FlowLossPercentange,
                        value: flowLossData ? flowLossData.percent : DEFAULT_FLOW_LOSS_PERCENTAGE,
                        disabled: flowLossData ? false : true,
                    }),
                    field.select<SamplerTypeOption, number>(ConfigureAlarmsFieldId.Sensitivity, 'sensitivity', {
                        panelClass: 'alarm-input-70',
                        label: 'COMMON.SENSITIVITY',
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.name,
                        value: flowLossData ? flowLossData.sensitivity : 1,
                        disabled: flowLossData ? false : true,
                        optionsSelector: SelectorIdentifier.FlowLossSensitivity,
                    }),
                    field.toggle(ConfigureAlarmsFieldId.ClearPatternHistory, 'clearPatternHistory', {
                        panelClass: 'alarm-slide-140',
                        placeholder: 'COMMON.RESET_PATTERN_HISTORY_ON_ACTIVATION',
                        value: flowLossData ? flowLossData.clearPatternHistory : false,
                        disabled: flowLossData ? false : true,
                    }),
                ],
            },
        ],
    };
};
